import {
  SapFlocRow,
  SapEquipmentRow,
  ParsedFloc,
  ParsedEquipment,
  SapImportConfig,
  ImportValidation,
  FlocType,
  FunctionalLocation,
} from '@easydone/shared';
import { collection, writeBatch, doc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Parse SAP FLOC code hierarchy
 * Examples:
 * - "ACT.043.001.001" -> Level 3 (4 segments)
 * - "BFP.000.001.002" -> Level 3 (4 segments)
 * - "DWT.001" -> Level 1 (2 segments)
 */
export function parseFlocHierarchy(code: string): {
  level: number;
  segments: string[];
  parentCode: string | null;
} {
  // Split by dot or dash (SAP uses different separators)
  const segments = code.split(/[.-]/);

  const level = segments.length - 1; // 0-based level

  // Parent is all segments except the last one
  const parentCode = level > 0 ? segments.slice(0, -1).join('.') : null;

  return {
    level,
    segments,
    parentCode,
  };
}

/**
 * Determine FLOC type based on code prefix and level
 */
export function determineFlocType(
  code: string,
  level: number,
  config: SapImportConfig
): FlocType {
  // Check custom type mapping first
  if (config.typeMapping) {
    for (const [prefix, type] of Object.entries(config.typeMapping)) {
      if (code.startsWith(prefix)) {
        return type;
      }
    }
  }

  // Default hierarchy based on level
  // Level 0 (root) = site
  // Level 1 = system or building (depending on prefix)
  // Level 2 = area or zone
  // Level 3+ = equipment_position

  if (level === 0) {
    return 'site';
  } else if (level === 1) {
    // Common SAP prefixes for systems
    const systemPrefixes = ['HVAC', 'ELEC', 'WATER', 'PROD', 'SYS'];
    const prefix = code.split(/[.-]/)[0];
    if (systemPrefixes.includes(prefix.toUpperCase())) {
      return 'system';
    }
    return 'building';
  } else if (level === 2) {
    return 'area';
  } else {
    return 'equipment_position';
  }
}

/**
 * Parse SAP FLOC rows into structured format
 * Auto-generates all intermediate parent FLOCs
 */
export function parseSapFlocs(
  rows: SapFlocRow[],
  config: SapImportConfig
): ParsedFloc[] {
  const codeMap = new Map<string, ParsedFloc>();

  // First pass: process all rows from the Excel file
  for (const row of rows) {
    const code = row['Functional Loc.'].trim();
    const name = row['Description'].trim();

    const { level, segments } = parseFlocHierarchy(code);
    const type = determineFlocType(code, level, config);

    const parsedFloc: ParsedFloc = {
      code,
      name,
      level,
      parentCode: null, // Will be set in second pass
      path: [],         // Will be set in second pass
      type,
      criticality: config.defaultCriticality,
      operationalStatus: config.defaultStatus,
      description: row['Description'],
    };

    codeMap.set(code, parsedFloc);

    // Auto-generate all intermediate parent FLOCs
    for (let i = 1; i < segments.length; i++) {
      const parentCode = segments.slice(0, i).join('.');

      if (!codeMap.has(parentCode)) {
        const parentLevel = i - 1;
        const parentType = determineFlocType(parentCode, parentLevel, config);

        codeMap.set(parentCode, {
          code: parentCode,
          name: `${parentCode} (Auto-generated)`,
          level: parentLevel,
          parentCode: null, // Will be set in second pass
          path: [],         // Will be set in second pass
          type: parentType,
          criticality: config.defaultCriticality,
          operationalStatus: config.defaultStatus,
          description: `Auto-generated parent FLOC for ${parentCode}`,
        });
      }
    }
  }

  // Second pass: establish parent-child relationships and build paths
  const allFlocs = Array.from(codeMap.values());

  // Sort by level to ensure parents are processed first
  allFlocs.sort((a, b) => a.level - b.level);

  for (const floc of allFlocs) {
    const { segments } = parseFlocHierarchy(floc.code);

    // Set parent code (all segments except the last one)
    if (segments.length > 1) {
      floc.parentCode = segments.slice(0, -1).join('.');

      // Build path array (codes of all ancestors)
      const path: string[] = [];
      for (let i = 1; i < segments.length; i++) {
        path.push(segments.slice(0, i).join('.'));
      }
      floc.path = path;
    }
  }

  return allFlocs;
}

/**
 * Parse SAP equipment rows
 */
export function parseSapEquipment(
  rows: SapEquipmentRow[],
  config: SapImportConfig
): ParsedEquipment[] {
  const parsed: ParsedEquipment[] = [];

  for (const row of rows) {
    const serialNumber = row['Equipment'].trim();
    const name = row['Description'].trim();

    // Check for FLOC reference in mapping or row data
    let flocCode: string | undefined;
    if (config.equipmentFlocMapping && config.equipmentFlocMapping[serialNumber]) {
      flocCode = config.equipmentFlocMapping[serialNumber];
    } else if (row['Functional Loc.']) {
      flocCode = row['Functional Loc.'].trim();
    }

    parsed.push({
      serialNumber,
      name,
      flocCode,
      description: row['Description'],
    });
  }

  return parsed;
}

/**
 * Validate parsed data before import
 */
export function validateImport(
  flocs: ParsedFloc[],
  equipment: ParsedEquipment[]
): ImportValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for duplicate codes
  const codes = new Set<string>();
  for (const floc of flocs) {
    if (codes.has(floc.code)) {
      errors.push(`Duplicate FLOC code: ${floc.code}`);
    }
    codes.add(floc.code);
  }

  // Check for orphaned FLOCs (missing parent)
  const codeSet = new Set(flocs.map(f => f.code));
  for (const floc of flocs) {
    if (floc.parentCode && !codeSet.has(floc.parentCode)) {
      warnings.push(`FLOC ${floc.code} references missing parent: ${floc.parentCode}`);
    }
  }

  // Check equipment references
  for (const equip of equipment) {
    if (equip.flocCode && !codeSet.has(equip.flocCode)) {
      warnings.push(`Equipment ${equip.serialNumber} references missing FLOC: ${equip.flocCode}`);
    }
  }

  // Calculate stats
  const rootFlocs = flocs.filter(f => f.level === 0);
  const maxDepth = Math.max(...flocs.map(f => f.level), 0);

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    stats: {
      totalFlocs: flocs.length,
      rootFlocs: rootFlocs.length,
      maxDepth,
      totalEquipment: equipment.length,
    },
  };
}

/**
 * Import FLOCs to Firestore
 */
export async function importFlocs(
  orgId: string,
  _siteId: string,
  flocs: ParsedFloc[],
  onProgress?: (current: number, total: number, message: string) => void
): Promise<{ success: boolean; imported: number; errors: string[] }> {
  const errors: string[] = [];
  let imported = 0;

  // Build map of code -> Firestore ID
  const codeToIdMap = new Map<string, string>();

  // Sort by level to ensure parents are created first
  const sortedFlocs = [...flocs].sort((a, b) => a.level - b.level);

  // Process in batches of 500 (Firestore limit)
  const batchSize = 500;
  for (let i = 0; i < sortedFlocs.length; i += batchSize) {
    const batch = writeBatch(db);
    const batchFlocs = sortedFlocs.slice(i, i + batchSize);

    for (const parsedFloc of batchFlocs) {
      try {
        const flocId = doc(collection(db, 'organizations', orgId, 'functionalLocations')).id;
        codeToIdMap.set(parsedFloc.code, flocId);

        // Resolve parent ID
        let parentFlocId: string | null = null;
        const pathIds: string[] = [];

        if (parsedFloc.parentCode) {
          parentFlocId = codeToIdMap.get(parsedFloc.parentCode) || null;

          // Build path IDs
          for (const parentCode of parsedFloc.path) {
            const id = codeToIdMap.get(parentCode);
            if (id) pathIds.push(id);
          }
        }

        const floc: Omit<FunctionalLocation, 'id'> = {
          orgId,
          code: parsedFloc.code,
          name: parsedFloc.name,
          description: parsedFloc.description,
          parentFlocId,
          level: parsedFloc.level,
          path: pathIds,
          type: parsedFloc.type,
          criticality: parsedFloc.criticality,
          operationalStatus: parsedFloc.operationalStatus,
          childCount: 0,
          installedAssetId: null,
          createdAt: Date.now() as any,
          updatedAt: Date.now() as any,
          createdBy: 'system',
          updatedBy: 'system',
        };

        const flocRef = doc(db, 'organizations', orgId, 'functionalLocations', flocId);
        batch.set(flocRef, floc);

        imported++;

        if (onProgress) {
          onProgress(i + batchFlocs.indexOf(parsedFloc) + 1, sortedFlocs.length, `Importing ${parsedFloc.code}...`);
        }
      } catch (err: any) {
        errors.push(`Failed to import ${parsedFloc.code}: ${err.message}`);
      }
    }

    await batch.commit();
  }

  // Update childCount for all parents
  await updateChildCounts(orgId);

  return {
    success: errors.length === 0,
    imported,
    errors,
  };
}

/**
 * Update childCount for all FLOCs
 */
async function updateChildCounts(orgId: string): Promise<void> {
  const flocsRef = collection(db, 'organizations', orgId, 'functionalLocations');
  const snapshot = await getDocs(flocsRef);

  const childCounts = new Map<string, number>();

  // Count children for each parent
  for (const doc of snapshot.docs) {
    const floc = doc.data() as FunctionalLocation;
    if (floc.parentFlocId) {
      childCounts.set(floc.parentFlocId, (childCounts.get(floc.parentFlocId) || 0) + 1);
    }
  }

  // Update in batches
  const batch = writeBatch(db);
  let count = 0;

  for (const [flocId, childCount] of childCounts.entries()) {
    const flocRef = doc(db, 'organizations', orgId, 'functionalLocations', flocId);
    batch.update(flocRef, { childCount });
    count++;

    if (count >= 500) {
      await batch.commit();
      count = 0;
    }
  }

  if (count > 0) {
    await batch.commit();
  }
}

/**
 * Import equipment/assets to Firestore
 */
export async function importEquipment(
  orgId: string,
  _siteId: string,
  equipment: ParsedEquipment[],
  _flocCodeToIdMap: Map<string, string>,
  onProgress?: (current: number, total: number, message: string) => void
): Promise<{ success: boolean; imported: number; errors: string[] }> {
  const errors: string[] = [];
  let imported = 0;

  const batchSize = 500;
  for (let i = 0; i < equipment.length; i += batchSize) {
    const batch = writeBatch(db);
    const batchEquipment = equipment.slice(i, i + batchSize);

    for (const parsedEquip of batchEquipment) {
      try {
        const assetId = doc(collection(db, 'organizations', orgId, 'assets')).id;

        // Build asset object, only including fields with values (no undefined)
        const asset: any = {
          orgId,
          serialNumber: parsedEquip.serialNumber,
          assetId: parsedEquip.serialNumber, // Use serial number as asset ID
          name: parsedEquip.name,
          type: 'Equipment',
          lifecycleStatus: 'in_storage',
          status: 'active',
          conditionScore: 75,
          conditionGrade: 'B',
          lastConditionUpdate: Date.now(),
          photos: [],
          documents: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          createdBy: 'system',
          updatedBy: 'system',
        };

        // Only add optional fields if they have values
        if (parsedEquip.description) {
          asset.description = parsedEquip.description;
        }
        if (parsedEquip.manufacturer) {
          asset.manufacturer = parsedEquip.manufacturer;
        }
        if (parsedEquip.model) {
          asset.model = parsedEquip.model;
        }

        const assetRef = doc(db, 'organizations', orgId, 'assets', assetId);
        batch.set(assetRef, asset);

        imported++;

        if (onProgress) {
          onProgress(i + batchEquipment.indexOf(parsedEquip) + 1, equipment.length, `Importing ${parsedEquip.serialNumber}...`);
        }
      } catch (err: any) {
        errors.push(`Failed to import ${parsedEquip.serialNumber}: ${err.message}`);
      }
    }

    await batch.commit();
  }

  return {
    success: errors.length === 0,
    imported,
    errors,
  };
}
